import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { monitoringAPI, tasksAPI, organizationsAPI } from '../services/api';
import { Loading } from '../components/common';
import { formatNumber, formatPercent } from '../utils/formatters';
import { ComputerDesktopIcon, CheckCircleIcon, ClockIcon, ExclamationCircleIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';

const Monitoring = () => {
  const { user } = useSelector((state) => state.auth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await monitoringAPI.getDashboard();
        setData(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    const interval = setInterval(fetchData, 30000); // Har 30 sekundda yangilash
    return () => clearInterval(interval);
  }, []);

  if (loading) return <Loading />;

  const completionRate = data?.total_tasks ? ((data.completed / data.total_tasks) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Real-time Monitoring</h1>
          <p className="text-gray-500">Topshiriqlar bajarilishini kuzatish</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          <span className="text-sm text-gray-500">Jonli</span>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card text-center">
          <ComputerDesktopIcon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-gray-800">{formatNumber(data?.total_tasks || 0)}</p>
          <p className="text-sm text-gray-500">Jami topshiriqlar</p>
        </div>
        <div className="card text-center">
          <CheckCircleIcon className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-green-600">{formatNumber(data?.completed || 0)}</p>
          <p className="text-sm text-gray-500">Bajarilgan</p>
        </div>
        <div className="card text-center">
          <ClockIcon className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-yellow-600">{formatNumber(data?.pending || 0)}</p>
          <p className="text-sm text-gray-500">Jarayonda</p>
        </div>
        <div className="card text-center">
          <ExclamationCircleIcon className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-red-600">{formatNumber(data?.overdue || 0)}</p>
          <p className="text-sm text-gray-500">Muddati o'tgan</p>
        </div>
        <div className="card text-center">
          <ExclamationCircleIcon className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <p className="text-3xl font-bold text-orange-600">{formatNumber(data?.deadline_soon || 0)}</p>
          <p className="text-sm text-gray-500">Muddat yaqin</p>
        </div>
      </div>

      {/* Progress */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4">Umumiy ijro foizi</h2>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div className="bg-green-500 h-4 rounded-full transition-all duration-500" style={{ width: `${completionRate}%` }}></div>
            </div>
          </div>
          <span className="text-2xl font-bold text-green-600">{completionRate}%</span>
        </div>
      </div>

      {/* By Organization */}
      {data?.by_organization?.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BuildingOfficeIcon className="w-5 h-5 text-blue-600" />
            Tashkilotlar bo'yicha
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="table-header">Tashkilot</th>
                  <th className="table-header text-center">Jami</th>
                  <th className="table-header text-center">Bajarilgan</th>
                  <th className="table-header text-center">Kutilmoqda</th>
                  <th className="table-header">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.by_organization.map((org, idx) => {
                  const orgCompletion = org.total ? ((org.completed / org.total) * 100) : 0;
                  return (
                    <tr key={idx}>
                      <td className="table-cell font-medium">{org.organization__short_name}</td>
                      <td className="table-cell text-center">{org.total}</td>
                      <td className="table-cell text-center text-green-600">{org.completed}</td>
                      <td className="table-cell text-center text-yellow-600">{org.pending}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full" style={{ width: `${orgCompletion}%` }}></div>
                          </div>
                          <span className="text-sm text-gray-600">{orgCompletion.toFixed(0)}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Monitoring;
